import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth-context';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, LayoutGrid, BarChart3, BellRing, Briefcase } from 'lucide-react';

// Bump this version whenever a new set of changes should be re-announced to all users.
const WHATS_NEW_VERSION = '2026-07';
const STORAGE_KEY = 'vx_whatsnew_seen';

const UPDATES = [
  {
    id: 'task-management',
    icon: LayoutGrid,
    title: 'New: Task Management module',
    body: 'Tasks now live in their own module (switch modules from the top of the sidebar). Try the new drag-and-drop Board to move tasks across To Do → In Progress → Blocked → Done.',
  },
  {
    id: 'lead-board',
    icon: Briefcase,
    title: 'Lead pipeline board',
    body: 'The Leads page has a new Board view — drag leads across pipeline stages. Use the List / Board toggle and the Due today / Overdue filters to focus your follow-ups.',
  },
  {
    id: 'lead-analytics',
    icon: BarChart3,
    title: 'Lead Analytics dashboard',
    body: 'Under Lead Management → Analytics: see your conversion funnel, win rate, and which campaigns and sources bring the most (and best-converting) leads.',
  },
  {
    id: 'smart-reminders',
    icon: BellRing,
    title: 'Smart reminders',
    body: 'The 🔔 bell now alerts you when a task is due tomorrow, due today, or overdue — and when a lead follow-up is due. Never lose track of a client again.',
  },
];

export function WhatsNewDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (seen !== WHATS_NEW_VERSION) {
      const t = setTimeout(() => setOpen(true), 700);
      return () => clearTimeout(t);
    }
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, WHATS_NEW_VERSION);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="max-w-lg" data-testid="whats-new-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Sparkles size={18} />
            </span>
            <div>
              <DialogTitle className="font-heading">What's new in Falcon</DialogTitle>
              <DialogDescription>We've shipped some upgrades — here's what changed.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
          {UPDATES.map((u) => {
            const Icon = u.icon;
            return (
              <div key={u.id} className="flex items-start gap-3 rounded-lg border p-3 bg-muted/30" data-testid={`whats-new-item-${u.id}`}>
                <span className="h-8 w-8 shrink-0 rounded-md bg-primary/10 text-primary flex items-center justify-center mt-0.5">
                  <Icon size={16} />
                </span>
                <div>
                  <p className="text-sm font-medium">{u.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{u.body}</p>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button onClick={dismiss} className="gap-2" data-testid="whats-new-dismiss">
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
